import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { cn } from "@hipotecar/ui/lib/utils";

export type CardValue = string;

export interface CardOption {
	value: CardValue;
	label: string;
	description: string;
	amount: number;
	disabled?: boolean;
	highlightColor?: "brand" | "success" | "warning" | "danger";
}

interface CardSelectorProps {
	value: CardValue;
	onChange: (value: CardValue) => void;
	options: CardOption[];
	title?: React.ReactNode;
	subtitle?: string;
	className?: string;
	name?: string;
	currency?: string;
	gridCols?: number;
	disabled?: boolean;
}

export function CardSelector({
	value,
	onChange,
	options,
	title,
	subtitle,
	className,
	currency = "S/",
	gridCols = 3,
	disabled = false,
}: CardSelectorProps) {
	const gridColsClass =
		{
			1: "md:grid-cols-1",
			2: "md:grid-cols-2",
			3: "md:grid-cols-2",
			4: "md:grid-cols-2",
			5: "md:grid-cols-5",
			6: "md:grid-cols-6",
		}[gridCols] || "md:grid-cols-3";

	return (
		<div className={className}>
			{(title || subtitle) && (
				<div className="mb-3">
					{title && <h3 className="heading-md">{title}</h3>}
					{subtitle && (
						<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
							{subtitle}
						</p>
					)}
				</div>
			)}

			<RadioGroup
				value={value}
				onValueChange={onChange}
				className={cn("grid gap-4", gridColsClass)}
			>
				{options.map((option) => {
					const isSelected = value === option.value;
					const isDisabled = disabled || option.disabled || false;
					const highlightColor = option.highlightColor || "brand";

					const colorClasses = {
						brand: {
							border: "data-[state=checked]:border-brand-500",
							bg: "data-[state=checked]:bg-brand-50 dark:data-[state=checked]:bg-brand-500/10",
							indicator: "bg-brand-500 border-brand-500",
						},
						success: {
							border: "data-[state=checked]:border-success-500",
							bg: "data-[state=checked]:bg-success-50 dark:data-[state=checked]:bg-success-500/10",
							indicator: "bg-success-500 border-success-500",
						},
						warning: {
							border: "data-[state=checked]:border-warning-500",
							bg: "data-[state=checked]:bg-warning-50 dark:data-[state=checked]:bg-warning-500/10",
							indicator: "bg-warning-500 border-warning-500",
						},
						danger: {
							border: "data-[state=checked]:border-danger-500",
							bg: "data-[state=checked]:bg-danger-50 dark:data-[state=checked]:bg-danger-500/10",
							indicator: "bg-danger-500 border-danger-500",
						},
					};

					const selectedColors = colorClasses[highlightColor];

					return (
						<Label
							key={option.value}
							htmlFor={option.value}
							className={cn(
								"relative flex h-full cursor-pointer flex-col rounded-xl border border-default bg-card p-4 shadow-sm transition-all dark:hover:bg-gray-800/50",
								selectedColors.border,
								selectedColors.bg,
								isDisabled && "hover:bg-card cursor-not-allowed opacity-50",
							)}
						>
							<RadioGroupItem
								value={option.value}
								id={option.value}
								className="sr-only"
								disabled={isDisabled}
							/>
							<div className="flex items-start justify-between">
								<div className="flex-1">
									<span className="heading-sm block">{option.label}</span>
									<span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
										{option.description}
									</span>
								</div>
								<div
									className={cn(
										"flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-gray-300 dark:border-gray-600",
										isSelected && selectedColors.indicator,
									)}
								>
									{isSelected && (
										<div className="size-2 rounded-full bg-white" />
									)}
								</div>
							</div>
							<div className="mt-auto pt-3">
								<span className="heading-lg text-lg font-bold">
									{currency}{" "}
									{option.amount.toLocaleString("en-US", {
										minimumFractionDigits: 2,
										maximumFractionDigits: 2,
									})}
								</span>
							</div>
						</Label>
					);
				})}
			</RadioGroup>
		</div>
	);
}
